import React, { useState, useEffect, useCallback } from 'react';
import { Icon } from '@iconify/react';
import { toast } from 'react-toastify';
import { Modal, Button, Form } from 'react-bootstrap';
import config from '../config';
import { useAuth } from '../context/AuthContext';
import VenueMenuCategories from './VenueMenuCategories';

const ItemsList = ({ items, handleEditItem, handleDeleteItem }) => (
    <div className="hs-table-wrapper">
        <table className="hs-table mb-0">
            <thead>
                <tr>
                    <th>Dish / Item</th>
                    <th>Price</th>
                    <th>Description</th>
                    <th className="text-end">Actions</th>
                </tr>
            </thead>
            <tbody>
                {items.map(item => (
                    <tr key={item.id}>
                        <td>
                            <div className="d-flex align-items-center gap-2">
                                <div className="w-32-px h-32-px rounded-circle bg-primary-50 text-primary-600 d-flex align-items-center justify-content-center flex-shrink-0">
                                    <Icon icon="solar:chef-hat-bold" className="text-base" />
                                </div>
                                <div className="d-flex flex-column">
                                    <span className="fw-bold text-dark text-sm">{item.name}</span>
                                    {item.name_ar && <span className="text-xs text-secondary-light" dir="rtl">{item.name_ar}</span>}
                                </div>
                            </div>
                        </td>
                        <td>
                            <span className="fw-bold text-primary-700 text-sm">
                                {item.price} <small className="text-xs text-secondary-light">{item.currency || 'AED'}</small>
                            </span>
                        </td>
                        <td className="text-truncate" style={{ maxWidth: '280px' }}>
                            <div className="d-flex flex-column">
                                <span className="text-truncate text-secondary-light text-xs">{item.description || '—'}</span>
                                {item.description_ar && <span className="text-xs text-secondary-light text-truncate" dir="rtl">{item.description_ar}</span>}
                            </div>
                        </td>
                        <td className="text-end">
                            <div className="d-inline-flex gap-2">
                                <button 
                                    className="hs-icon-btn hs-btn-sm"
                                    onClick={() => handleEditItem(item)}
                                    title="Edit Item"
                                    aria-label="Edit Item"
                                >
                                    <Icon icon="solar:pen-bold" />
                                </button>
                                <button 
                                    className="hs-icon-btn hs-btn-sm text-danger-600"
                                    onClick={() => handleDeleteItem(item.id)}
                                    title="Delete Item"
                                    aria-label="Delete Item"
                                >
                                    <Icon icon="solar:trash-bin-trash-bold" />
                                </button>
                            </div>
                        </td>
                    </tr>
                ))}
            </tbody>
        </table>
    </div>
);

const FileUploadManager = ({ menu, type, accept, label, auth, onUploadSuccess, handleDeleteMenu }) => {
    const [file, setFile] = useState(null);
    const [uploading, setUploading] = useState(false);

    const handleFileChange = (e) => {
        if (e.target.files[0]) {
            setFile(e.target.files[0]);
        }
    };

    const handleUpload = async () => {
        if (!file) return;

        const formData = new FormData();
        formData.append('file', file);

        setUploading(true);
        try {
            let token = auth.token;
            const localToken = localStorage.getItem('token');
            const hubscrollToken = localStorage.getItem('hubscroll_token') || localStorage.getItem('vscroll_auth_token');

            if (!token) {
                 token = hubscrollToken || localToken;
            }
            
            if (!token) {
                toast.error('Authentication error. Please log in again.');
                setUploading(false);
                return;
            }

            const endpoint = type === 'csv' 
                ? `${config.API_BASE_URL}/api/menus/${menu.id}/upload-csv`
                : type === 'pdf'
                    ? `${config.API_BASE_URL}/api/menus/${menu.id}/upload-pdf`
                    : `${config.API_BASE_URL}/api/menus/${menu.id}/upload`;

            const res = await fetch(endpoint, {
                method: 'POST',
                headers: {
                    'Authorization': `Bearer ${token}`
                },
                body: formData
            });

            if (res.ok) {
                const data = await res.json();
                if (type === 'csv') {
                    toast.success(`Successfully uploaded ${data.count || 0} items`);
                } else {
                    toast.success('File uploaded successfully');
                }
                onUploadSuccess(data);
                setFile(null);
            } else {
                const err = await res.json();
                toast.error(err.message || 'Upload failed');
            }
        } catch (error) {
            console.error(error);
            toast.error('Upload failed');
        } finally {
            setUploading(false);
        }
    };

    return (
        <div className="card h-100 radius-12 border-0 shadow-sm">
            <div className="card-header d-flex justify-content-between align-items-center bg-white py-16 px-24 border-bottom">
                <h5 className="mb-0 fw-bold text-dark">{label} Manager</h5>
                {handleDeleteMenu && (
                    <button className="btn btn-outline-danger btn-sm radius-8" onClick={handleDeleteMenu}>Reset Menu</button>
                )}
            </div>
            <div className="card-body text-center p-5">
                <Icon icon="mdi:cloud-upload" className="text-6xl text-secondary-light mb-3" />
                <h5 className="mb-2 fw-bold text-dark">{label} Upload</h5>
                <p className="text-secondary-light mb-4">Upload your digital {label} file here.</p>
                
                {type === 'csv' && (
                    <div className="mb-4">
                        <a href={`${config.API_BASE_URL}/api/menus/template/csv`} className="btn btn-outline-primary btn-sm radius-8">
                            <Icon icon="mdi:download" className="me-1" /> Download CSV Template
                        </a>
                        <p className="text-xs text-secondary-light mt-2">
                            Supported columns: Name, Name_AR, Price, Description, Description_AR, Category
                        </p>
                    </div>
                )}

                <div className="border border-dashed border-primary-200 rounded-3 p-5 bg-primary-50 mb-4">
                    <input 
                        type="file" 
                        accept={accept} 
                        onChange={handleFileChange} 
                        className="form-control mb-3 bg-white radius-8"
                    />
                    <button 
                        className="btn btn-primary-600 w-100 radius-8 fw-semibold" 
                        onClick={handleUpload} 
                        disabled={!file || uploading}
                    >
                        {uploading ? 'Uploading...' : 'Upload File'}
                    </button>
                </div>
            </div>
        </div>
    );
};

const ImageManager = ({ images, setImages, handleDeleteMenu }) => (
    <div className="card h-100 radius-12 border-0 shadow-sm">
        <div className="card-header d-flex justify-content-between align-items-center bg-white py-16 px-24 border-bottom">
            <h5 className="mb-0 fw-bold text-dark">Image Menu Manager</h5>
            {handleDeleteMenu && (
                <button className="btn btn-outline-danger btn-sm radius-8" onClick={handleDeleteMenu}>Reset Menu</button>
            )}
        </div>
        <div className="card-body text-center p-5">
            <Icon icon="mdi:image-multiple" className="text-6xl text-secondary-light mb-3" />
            <h5 className="mb-2 fw-bold text-dark">Image Menu Gallery</h5>
            <p className="text-secondary-light mb-4">Upload high resolution photos of your printed menu pages.</p>
             <div className="border border-dashed border-primary-200 rounded-3 p-5 bg-primary-50">
                <p className="mb-0 text-primary-600 fw-medium">Upload photo menu feature active</p>
            </div>
        </div>
    </div>
);

const ManualManager = ({ menu, items, setItems, categories, setCategories, auth, handleDeleteMenu, showCategoryManager, setShowCategoryManager, title }) => {
    const [newItem, setNewItem] = useState({ 
        name: '', 
        name_ar: '',
        price: '', 
        description: '', 
        description_ar: '',
        category_id: '' 
    });

    // Edit Item Modal State
    const [showEditItemModal, setShowEditItemModal] = useState(false);
    const [editingItem, setEditingItem] = useState(null);
    const [editItemForm, setEditItemForm] = useState({
        name: '',
        name_ar: '',
        price: '',
        description: '',
        description_ar: '',
        category_id: ''
    });
    const [isSavingEdit, setIsSavingEdit] = useState(false);
    
    const categoryOptions = React.useMemo(() => {
        const options = [];
        const mainCategories = categories.filter(c => !c.parent_id).sort((a, b) => a.display_order - b.display_order);
        mainCategories.forEach(main => {
            options.push({ ...main, level: 0 });
            const subCategories = categories.filter(c => c.parent_id === main.id).sort((a, b) => a.display_order - b.display_order);
            subCategories.forEach(sub => options.push({ ...sub, level: 1 }));
        });
        return options;
    }, [categories]);

    const handleAddItem = async (e) => {
        e.preventDefault();
        try {
            const res = await auth.fetch(`${config.API_BASE_URL}/api/menus/${menu.id}/items`, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({ ...newItem, currency: 'AED' })
            });
            if (res.ok) {
                const savedItem = await res.json();
                setItems([...items, savedItem]);
                setNewItem({ 
                    name: '', 
                    name_ar: '',
                    price: '', 
                    description: '', 
                    description_ar: '',
                    category_id: '' 
                });
                toast.success('Menu item added successfully');
            }
        } catch (err) {
            toast.error('Failed to add item');
        }
    };

    const handleOpenEditItem = (item) => {
        setEditingItem(item);
        setEditItemForm({
            name: item.name || '',
            name_ar: item.name_ar || '',
            price: item.price || '',
            description: item.description || '',
            description_ar: item.description_ar || '',
            category_id: item.category_id || ''
        });
        setShowEditItemModal(true);
    };

    const handleSaveEditItem = async (e) => {
        e.preventDefault();
        if (!editingItem) return;

        setIsSavingEdit(true);
        try {
            const res = await auth.fetch(`${config.API_BASE_URL}/api/menus/items/${editingItem.id}`, {
                method: 'PUT',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({ ...editItemForm, currency: 'AED' })
            });

            if (res.ok) {
                const updatedItem = await res.json();
                setItems(prev => prev.map(item => item.id === editingItem.id ? updatedItem : item));
                toast.success('Menu item updated successfully');
                setShowEditItemModal(false);
                setEditingItem(null);
            } else {
                toast.error('Failed to update menu item');
            }
        } catch (err) {
            console.error(err);
            toast.error('Error saving item changes');
        } finally {
            setIsSavingEdit(false);
        }
    };

    const handleDeleteItem = async (itemId) => {
        if (!window.confirm('Are you sure you want to delete this menu item?')) return;
        try {
            const res = await auth.fetch(`${config.API_BASE_URL}/api/menus/items/${itemId}`, { method: 'DELETE' });
            if (res.ok) {
                setItems(items.filter(i => i.id !== itemId));
                toast.success('Menu item deleted');
            }
        } catch (err) {
            toast.error('Failed to delete item');
        }
    };

    if (showCategoryManager) {
        return (
            <div className="card radius-12 border-0 shadow-sm">
                 <div className="card-header d-flex justify-content-between align-items-center bg-white py-16 px-24 border-bottom">
                    <h6 className="mb-0 fw-bold text-dark">Manage Menu Categories</h6>
                    <button className="btn btn-outline-primary btn-sm radius-8" onClick={() => setShowCategoryManager(false)}>
                        <Icon icon="mdi:arrow-left" className="me-1" /> Back to Menu Items
                    </button>
                </div>
                <div className="card-body p-24">
                    <VenueMenuCategories 
                        menuId={menu.id} 
                        onCategoriesChange={setCategories} 
                    />
                </div>
            </div>
        );
    }

    const renderItemsList = (itemList) => (
        <ItemsList items={itemList} handleEditItem={handleOpenEditItem} handleDeleteItem={handleDeleteItem} />
    );

    return (
        <div className="card radius-12 border-0 shadow-sm">
            <div className="card-header d-flex justify-content-between align-items-center bg-white py-16 px-24 border-bottom">
                <div>
                    <h6 className="mb-0 fw-bold text-dark">{title || 'Digital Menu Items'}</h6>
                    <span className="text-xs text-secondary-light">Add, edit prices, or organize categories (all in AED)</span>
                </div>
                <div className="d-flex gap-2">
                    <button className="btn btn-primary-600 btn-sm radius-8 d-flex align-items-center gap-1 fw-semibold" onClick={() => setShowCategoryManager(true)}>
                        <Icon icon="mdi:shape" /> Organize Categories
                    </button>
                    {handleDeleteMenu && (
                        <button className="btn btn-outline-danger btn-sm radius-8" onClick={handleDeleteMenu}>Reset Menu</button>
                    )}
                </div>
            </div>
            <div className="card-body p-24">
                {/* Add New Item Form */}
                <form onSubmit={handleAddItem} className="row g-3 mb-24 p-20 bg-neutral-50 rounded-12 border">
                    <div className="col-12 d-flex align-items-center gap-2">
                        <Icon icon="solar:chef-hat-bold" className="text-primary-600 text-lg" />
                        <span className="fw-bold text-dark text-sm">Add New Dish / Drink</span>
                    </div>
                    <div className="col-md-3">
                        <input type="text" className="hs-input" placeholder="Item Name (English) *" required 
                            value={newItem.name} onChange={e => setNewItem({...newItem, name: e.target.value})} />
                    </div>
                    <div className="col-md-3">
                        <input type="text" className="hs-input" placeholder="Item Name (Arabic)" dir="rtl"
                            value={newItem.name_ar} onChange={e => setNewItem({...newItem, name_ar: e.target.value})} />
                    </div>
                    <div className="col-md-2">
                        <input type="number" step="0.5" className="hs-input" placeholder="Price (AED) *" required 
                            value={newItem.price} onChange={e => setNewItem({...newItem, price: e.target.value})} />
                    </div>
                    <div className="col-md-4">
                        <select 
                            className="hs-select" 
                            value={newItem.category_id} 
                            onChange={e => setNewItem({...newItem, category_id: e.target.value})}
                        >
                            <option value="">Select Category (Optional)</option>
                            {categoryOptions.map(cat => (
                                <option key={cat.id} value={cat.id}>
                                    {cat.level > 0 ? '\u00A0\u00A0\u00A0\u00A0' : ''}{cat.name}
                                </option>
                            ))}
                        </select>
                    </div>
                    <div className="col-md-5">
                        <input type="text" className="hs-input" placeholder="Description (English)" 
                            value={newItem.description} onChange={e => setNewItem({...newItem, description: e.target.value})} />
                    </div>
                    <div className="col-md-5">
                        <input type="text" className="hs-input" placeholder="Description (Arabic)" dir="rtl"
                            value={newItem.description_ar} onChange={e => setNewItem({...newItem, description_ar: e.target.value})} />
                    </div>
                    <div className="col-md-2">
                        <button type="submit" className="hs-btn-primary w-100">
                            <Icon icon="solar:add-circle-bold" /> Add Dish
                        </button>
                    </div>
                </form>

                {/* Categorized Items List */}
                <div className="d-flex flex-column gap-20">
                    {items.filter(i => !i.category_id).length > 0 && (
                        <div className="border rounded-3 p-16 bg-white shadow-2xs">
                            <h6 className="mb-12 fw-bold text-secondary-light text-sm">General / Uncategorized</h6>
                            {renderItemsList(items.filter(i => !i.category_id))}
                        </div>
                    )}
                    
                    {categories.filter(c => !c.parent_id).sort((a, b) => a.display_order - b.display_order).map(main => {
                        const mainItems = items.filter(i => Number(i.category_id) === main.id);
                        const subCategories = categories.filter(c => c.parent_id === main.id).sort((a, b) => a.display_order - b.display_order);
                        
                        if (mainItems.length === 0 && subCategories.length === 0) return null;

                        return (
                            <div key={main.id} className="border rounded-3 overflow-hidden shadow-2xs">
                                <div className="bg-light p-12 d-flex align-items-center gap-2 border-bottom">
                                    {main.icon && <Icon icon={main.icon} className="text-xl" style={{color: main.color || 'inherit'}} />}
                                    <h6 className="mb-0 fw-bold text-dark text-sm">{main.name}</h6>
                                    <span className="badge bg-white text-dark border rounded-pill ms-auto text-xs">
                                        {mainItems.length + items.filter(i => subCategories.some(s => s.id === Number(i.category_id))).length} Items
                                    </span>
                                </div>
                                
                                {mainItems.length > 0 && <div className="p-16 bg-white">{renderItemsList(mainItems)}</div>}
                                
                                {subCategories.map(sub => {
                                    const subItems = items.filter(i => Number(i.category_id) === sub.id);
                                    if (subItems.length === 0) return null;
                                    return (
                                        <div key={sub.id} className="mx-16 my-12 border-start border-3 border-primary-400 ps-16">
                                            <div className="d-flex align-items-center gap-2 mb-8">
                                                {sub.icon && <Icon icon={sub.icon} className="text-md" style={{color: sub.color || 'inherit'}} />}
                                                <span className="fw-semibold text-primary-600 text-xs">{sub.name}</span>
                                            </div>
                                            {renderItemsList(subItems)}
                                        </div>
                                    );
                                })}
                            </div>
                        );
                    })}
                    
                    {items.length === 0 && (
                        <div className="hs-empty-state">
                            <div className="hs-empty-icon">
                                <Icon icon="solar:chef-hat-bold" />
                            </div>
                            <h5 className="hs-empty-title">No Menu Items Listed</h5>
                            <p className="hs-empty-desc">Use the form above to add your first dish, beverage, or specialty.</p>
                        </div>
                    )}
                </div>
            </div>

            {/* Modal: Edit Existing Item */}
            <Modal show={showEditItemModal} onHide={() => setShowEditItemModal(false)} centered>
                <Modal.Header closeButton className="border-bottom bg-white py-16 px-24">
                    <Modal.Title className="text-lg fw-bold text-dark mb-0">Edit Menu Item</Modal.Title>
                </Modal.Header>
                <Form onSubmit={handleSaveEditItem}>
                    <Modal.Body className="p-24">
                        <div className="d-flex flex-column gap-3">
                            <div>
                                <Form.Label className="fw-semibold text-dark text-sm mb-1">Item Name (English) *</Form.Label>
                                <Form.Control 
                                    type="text" 
                                    required
                                    value={editItemForm.name}
                                    onChange={(e) => setEditItemForm({ ...editItemForm, name: e.target.value })}
                                    className="bg-base radius-8"
                                />
                            </div>

                            <div>
                                <Form.Label className="fw-semibold text-dark text-sm mb-1">Item Name (Arabic)</Form.Label>
                                <Form.Control 
                                    type="text" 
                                    dir="rtl"
                                    value={editItemForm.name_ar}
                                    onChange={(e) => setEditItemForm({ ...editItemForm, name_ar: e.target.value })}
                                    className="bg-base radius-8"
                                />
                            </div>

                            <div>
                                <Form.Label className="fw-semibold text-dark text-sm mb-1">Price (AED) *</Form.Label>
                                <Form.Control 
                                    type="number"
                                    step="0.5"
                                    required
                                    value={editItemForm.price}
                                    onChange={(e) => setEditItemForm({ ...editItemForm, price: e.target.value })}
                                    className="bg-base radius-8"
                                />
                            </div>

                            <div>
                                <Form.Label className="fw-semibold text-dark text-sm mb-1">Category</Form.Label>
                                <Form.Select 
                                    value={editItemForm.category_id}
                                    onChange={(e) => setEditItemForm({ ...editItemForm, category_id: e.target.value })}
                                    className="bg-base radius-8"
                                >
                                    <option value="">General / Uncategorized</option>
                                    {categoryOptions.map(cat => (
                                        <option key={cat.id} value={cat.id}>
                                            {cat.level > 0 ? '\u00A0\u00A0\u00A0\u00A0' : ''}{cat.name}
                                        </option>
                                    ))}
                                </Form.Select>
                            </div>

                            <div>
                                <Form.Label className="fw-semibold text-dark text-sm mb-1">Description (English)</Form.Label>
                                <Form.Control 
                                    as="textarea"
                                    rows={2}
                                    value={editItemForm.description}
                                    onChange={(e) => setEditItemForm({ ...editItemForm, description: e.target.value })}
                                    className="bg-base radius-8"
                                />
                            </div>

                            <div>
                                <Form.Label className="fw-semibold text-dark text-sm mb-1">Description (Arabic)</Form.Label>
                                <Form.Control 
                                    as="textarea"
                                    rows={2}
                                    dir="rtl"
                                    value={editItemForm.description_ar}
                                    onChange={(e) => setEditItemForm({ ...editItemForm, description_ar: e.target.value })}
                                    className="bg-base radius-8"
                                />
                            </div>
                        </div>
                    </Modal.Body>
                    <Modal.Footer className="border-top bg-white py-12 px-24">
                        <button type="button" className="hs-btn-secondary" onClick={() => setShowEditItemModal(false)}>
                            Cancel
                        </button>
                        <Button variant="primary" type="submit" className="radius-8 fw-semibold" disabled={isSavingEdit}>
                            {isSavingEdit ? 'Saving...' : 'Save Changes'}
                        </Button>
                    </Modal.Footer>
                </Form>
            </Modal>
        </div>
    );
};

const VenueMenuManager = ({ venueId }) => {
    const { auth } = useAuth();
    const [menu, setMenu] = useState(null);
    const [loading, setLoading] = useState(true);
    const [items, setItems] = useState([]);
    const [images, setImages] = useState([]);
    const [categories, setCategories] = useState([]);
    const [showCategoryManager, setShowCategoryManager] = useState(false);

    // Fetch Menu Data
    const fetchMenu = useCallback(async () => {
        try {
            setLoading(true);
            const res = await auth.fetch(`${config.API_BASE_URL}/api/menus/venue/${venueId}`);
            if (res.ok) {
                const data = await res.json();
                setMenu(data.menu);
                setItems(data.items || []);
                setImages(data.images || []);
                setCategories(data.categories || []);
            } else if (res.status === 404) {
                setMenu(null);
            } else {
                throw new Error('Failed to fetch menu');
            }
        } catch (error) {
            console.error('Error fetching menu:', error);
        } finally {
            setLoading(false);
        }
    }, [auth, venueId]);

    useEffect(() => {
        fetchMenu();
    }, [fetchMenu]);

    const handleCreateMenu = async (method) => {
        if (!window.confirm(`Create a ${method.toUpperCase()} menu for this venue?`)) return;

        try {
            const res = await auth.fetch(`${config.API_BASE_URL}/api/menus`, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({ venue_id: venueId, method })
            });
            if (res.ok) {
                const data = await res.json();
                setMenu(data);
                setItems([]);
                setImages([]);
                setCategories([]);
                toast.success('Menu created successfully');
            } else {
                toast.error('Failed to create menu');
            }
        } catch (error) {
            console.error(error);
            toast.error('Error creating menu');
        }
    };

    const handleDeleteMenu = async () => {
        if (!window.confirm('Are you sure? This will delete all menu items permanently.')) return;
        try {
            const res = await auth.fetch(`${config.API_BASE_URL}/api/menus/${menu.id}`, {
                method: 'DELETE'
            });
            if (res.ok) {
                setMenu(null);
                setItems([]);
                setImages([]);
                setCategories([]);
                toast.success('Menu deleted');
            }
        } catch (error) {
            toast.error('Failed to delete menu');
        }
    };

    if (loading) {
        return (
            <div className="text-center py-5">
                <div className="spinner-border text-primary" role="status">
                    <span className="visually-hidden">Loading menu...</span>
                </div>
            </div>
        );
    }

    if (!menu) {
        return (
            <div className="card radius-12 border-0 shadow-sm p-4 text-center">
                <h5 className="fw-bold mb-3">Choose Menu Type for this Venue</h5>
                <p className="text-secondary-light mb-4">Select how you want to build and manage the digital menu.</p>
                <div className="row g-3 justify-content-center">
                    <div className="col-md-4">
                        <div className="card h-100 p-3 border hover-shadow cursor-pointer radius-8" onClick={() => handleCreateMenu('manual')}>
                            <Icon icon="solar:hamburger-menu-bold" className="text-4xl text-primary mb-2 mx-auto" />
                            <h6 className="fw-bold">Interactive Digital Menu</h6>
                            <p className="text-xs text-secondary-light">Add items with prices, categories, and descriptions manually.</p>
                            <button className="btn btn-sm btn-primary radius-8 mt-auto">Create Digital Menu</button>
                        </div>
                    </div>
                    <div className="col-md-4">
                        <div className="card h-100 p-3 border hover-shadow cursor-pointer radius-8" onClick={() => handleCreateMenu('csv')}>
                            <Icon icon="solar:file-spreadsheet-bold" className="text-4xl text-success mb-2 mx-auto" />
                            <h6 className="fw-bold">CSV Import</h6>
                            <p className="text-xs text-secondary-light">Upload entire menu spreadsheet via Excel / CSV.</p>
                            <button className="btn btn-sm btn-success radius-8 mt-auto">Upload CSV</button>
                        </div>
                    </div>
                    <div className="col-md-4">
                        <div className="card h-100 p-3 border hover-shadow cursor-pointer radius-8" onClick={() => handleCreateMenu('pdf')}>
                            <Icon icon="solar:document-bold" className="text-4xl text-danger mb-2 mx-auto" />
                            <h6 className="fw-bold">PDF Menu</h6>
                            <p className="text-xs text-secondary-light">Upload PDF document for customers to view.</p>
                            <button className="btn btn-sm btn-danger radius-8 mt-auto">Upload PDF</button>
                        </div>
                    </div>
                </div>
            </div>
        );
    }

    if (menu.method === 'manual') {
        return (
            <ManualManager
                menu={menu}
                items={items}
                setItems={setItems}
                categories={categories}
                setCategories={setCategories}
                auth={auth}
                handleDeleteMenu={handleDeleteMenu}
                showCategoryManager={showCategoryManager}
                setShowCategoryManager={setShowCategoryManager}
            />
        );
    }

    if (menu.method === 'csv') {
        return (
            <div className="d-flex flex-column gap-4">
                <FileUploadManager
                    menu={menu}
                    type="csv"
                    accept=".csv"
                    label="CSV Menu"
                    auth={auth}
                    onUploadSuccess={(data) => {
                        fetchMenu();
                    }}
                    handleDeleteMenu={handleDeleteMenu}
                />
                {items.length > 0 && (
                    <ManualManager
                        menu={menu}
                        items={items}
                        setItems={setItems}
                        categories={categories}
                        setCategories={setCategories}
                        auth={auth}
                        showCategoryManager={showCategoryManager}
                        setShowCategoryManager={setShowCategoryManager}
                        title="Imported Menu Items (Editable)"
                    />
                )}
            </div>
        );
    }

    if (menu.method === 'pdf') {
        return (
            <FileUploadManager
                menu={menu}
                type="pdf"
                accept=".pdf"
                label="PDF Menu"
                auth={auth}
                onUploadSuccess={(data) => {
                    fetchMenu();
                }}
                handleDeleteMenu={handleDeleteMenu}
            />
        );
    }

    return null;
};

export default VenueMenuManager;
